import type {CustomActions} from '@wix/component-protocol/schema' // eslint-disable-line wix-editor/no-internal-import
import {mapValues, pickBy} from 'lodash'
import {BaseActionsBuilder} from './BaseActionsBuilder'
import type {ManifestFeatureGfppDesktopConfig, TranslatableString} from '@wix/document-services-types'
import type {ManifestActionValue} from './types'
import {ActionsHelpers} from './ActionsHelpers'

export class CustomActionsBuilder extends BaseActionsBuilder<CustomActions> {
    private readonly ActionIds = {
        mainAction1: 'design',
        mainAction2: 'design'
    }

    private isActionNameExistInActionIds = (actionName: string): actionName is keyof typeof this.ActionIds => {
        return actionName in this.ActionIds
    }

    protected pickSupportedActions = (data: ManifestFeatureGfppDesktopConfig): Record<string, ManifestActionValue> => {
        const {mainActions, supportedIconButtons} = ActionsHelpers.prepareCustomActionValues(data)

        const customActions = data.iconButtons
            ? pickBy(data.iconButtons, (value, key) => {
                  const isSupported = supportedIconButtons.has(key)
                  const isActionIdExist = value && 'actionId' in value && !!value.actionId
                  return isSupported && isActionIdExist
              })
            : {}
        return {...mainActions, ...customActions}
    }

    private readonly labelCache = new Map<string, TranslatableString>()

    private getLabel = (key: string): TranslatableString => {
        if (!this.labelCache.has(key)) {
            this.labelCache.set(key, {
                value: ActionsHelpers.customActionsLabels.get(key) ?? '',
                shouldTranslate: false
            })
        }
        return this.labelCache.get(key)!
    }

    private getResolvedAction = (action: ManifestActionValue, key: string): ManifestActionValue => {
        if (action?.behavior?.startsWith('DEFAULT') && this.isActionNameExistInActionIds(key)) {
            return {
                actionId: this.ActionIds[key],
                behavior: 'CUSTOM_ACTION',
                label: this.getLabel('DEFAULT')
            }
        }

        const isLabelInAction = 'label' in action
        if (!isLabelInAction) {
            return {
                ...action,
                behavior: 'CUSTOM_ACTION',
                label: this.getLabel(key)
            }
        }

        return action
    }

    protected transformToBuilderActions(data: Record<string, ManifestActionValue>) {
        return mapValues(data, (action, key) => this.actionExecutionBuilder.build(this.getResolvedAction(action, key)))
    }
}
